import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
    AlertTriangle,
    Eye,
    EyeOff,
    ExternalLink,
    Loader2,
    Lock,
    RefreshCw,
    Trash2,
    FileText,
} from 'lucide-react';
import {
    useGetUserById,
    useResetPassword,
    useUpdateUserField,
    useUsers,
} from '@/api/users/use-users.ts';
import { useRoles } from '@/api/roles/use-roles.ts';
import {
    useUserNotes,
    useBulkDeleteUserNotes,
    useDeleteAllUserNotes,
    type UserNote,
    type UserNoteType,
} from '@/api/notes/use-user-notes.tsx';
import { useSystemConfiguration } from '@/api/system-configuration/use-system-configuration';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { PageContainer, PageHeader } from '@/components/shared/layout';
import DateTimeFormatter from '@/components/shared/DateTimeFormatter';
import DataTable, { Column } from '@/components/ui/data-table';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';

import { InlineEdit } from '@/components/ui/inline-edit';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ModulePermission, PermissionAction } from '@/types/permissions.ts';
import { usePermissions } from '@/hooks/usePermissions';
import { useDebounce } from '@/hooks/useDebounce';
import { useConfirm } from '@/hooks/useConfirm';
import { generatePassword } from '@/utils/passwordGenerator';

const UserEditPage: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { enqueueSnackbar } = useSnackbar();
    const { hasPermission } = usePermissions();
    const confirm = useConfirm();
    const canDeleteNotes = hasPermission(ModulePermission.NOTES, PermissionAction.DELETE);
    const { roles, isLoading: rolesLoading } = useRoles();
    const { data: usersResponse } = useUsers({ status: 'active' });
    const users = usersResponse?.data || [];
    const updateUserField = useUpdateUserField();
    const resetPassword = useResetPassword();

    // State for dialogs
    const [passwordResetDialog, setPasswordResetDialog] = useState(false);
    const [passwordData, setPasswordData] = useState({
        password: '',
    });
    const [showPassword, setShowPassword] = useState(false);
    const [selectedNotes, setSelectedNotes] = useState<string[]>([]);
    const [showNotesManagement, setShowNotesManagement] = useState(false);
    const [notesPage, setNotesPage] = useState(1);
    const [notesPageSize, setNotesPageSize] = useState(25);
    const [notesSearch, setNotesSearch] = useState('');
    const [notesTypeFilter, setNotesTypeFilter] = useState<string>('all');
    const [notesSort, setNotesSort] = useState<{ key: keyof UserNote; direction: 'asc' | 'desc' }>({
        key: 'created_at',
        direction: 'desc',
    });
    const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);

    const { data: systemConfig } = useSystemConfiguration();
    const tenantLocale = systemConfig?.locale ?? 'en-GB';
    const debouncedNotesSearch = useDebounce(notesSearch, 300);

    const { data: user, isLoading: userLoading, error: userError } = useGetUserById(id!);
    const { data: userNotes, isLoading: notesLoading } = useUserNotes(id!, {
        page: notesPage,
        per_page: notesPageSize,
        search: debouncedNotesSearch || undefined,
        type:
            notesTypeFilter !== 'all'
                ? (notesTypeFilter as 'note' | 'task' | 'activity')
                : undefined,
        sort_by: notesSort.key as string,
        sort_direction: notesSort.direction,
    });
    const bulkDeleteNotes = useBulkDeleteUserNotes();
    const deleteAllNotes = useDeleteAllUserNotes();

    const deleteAllTypes = useMemo((): UserNoteType[] => {
        if (
            notesTypeFilter === 'note' ||
            notesTypeFilter === 'task' ||
            notesTypeFilter === 'activity'
        ) {
            return [notesTypeFilter];
        }
        return ['note', 'task'];
    }, [notesTypeFilter]);

    const deleteAllLabel = useMemo(() => {
        if (notesTypeFilter === 'note') return 'Delete All Notes';
        if (notesTypeFilter === 'task') return 'Delete All Tasks';
        if (notesTypeFilter === 'activity') return 'Delete All Activities';
        return 'Delete All Tasks & Notes';
    }, [notesTypeFilter]);

    const deleteAllDescription = useMemo(() => {
        const count = userNotes?.meta?.total ?? 0;
        const countText = count > 0 ? ` (${count} item${count !== 1 ? 's' : ''})` : '';

        if (notesTypeFilter === 'note') {
            return `This will permanently delete all notes created by ${user?.name ?? 'this employee'}${countText}. This action cannot be undone.`;
        }
        if (notesTypeFilter === 'task') {
            return `This will permanently delete all tasks created by ${user?.name ?? 'this employee'}${countText}. This action cannot be undone.`;
        }
        if (notesTypeFilter === 'activity') {
            return `This will permanently delete all activities created by ${user?.name ?? 'this employee'}${countText}. This action cannot be undone.`;
        }
        return `This will permanently delete all tasks and notes created by ${user?.name ?? 'this employee'}. Activities will not be deleted. This action cannot be undone.`;
    }, [notesTypeFilter, user?.name, userNotes?.meta?.total]);

    const handleFieldUpdate = async (field: string, value: any) => {
        if (!id) return;

        try {
            await updateUserField.mutateAsync({ userId: id, field, value });
            enqueueSnackbar('Employee updated successfully', { variant: 'success' });
        } catch (error: any) {
            const message = error.response?.data?.message || 'Failed to update employee';
            enqueueSnackbar(message, { variant: 'error' });
        }
    };

    const handleCancel = () => {
        navigate('/users');
    };

    const handleNoteSelection = (noteId: string, checked: boolean) => {
        if (checked) {
            setSelectedNotes(prev => [...prev, noteId]);
        } else {
            setSelectedNotes(prev => prev.filter(id => id !== noteId));
        }
    };

    const handleSelectAllNotes = (checked: boolean) => {
        if (checked && userNotes?.data) {
            setSelectedNotes(userNotes.data.map(note => note.id));
        } else {
            setSelectedNotes([]);
        }
    };

    // Build lead/customer detail URL from notable_type (e.g. "App\Models\Lead" -> /leads/:id)
    const getNotableDetailUrl = (note: UserNote): string | null => {
        if (!note.notable?.id) return null;
        const type = note.notable_type ?? '';
        if (type.includes('Lead')) return `/leads/${note.notable.id}`;
        if (type.includes('Customer')) return `/customers/${note.notable.id}`;
        return null;
    };

    const notesColumns: Column<UserNote>[] = useMemo(
        () => [
            {
                id: 'notable',
                label: 'Customer / Lead',
                key: 'notable',
                sortable: false,
                width: '180px',
                render: (_value, note) => {
                    const url = getNotableDetailUrl(note);
                    const name = note.notable?.name || note.notable?.email || '—';
                    if (url) {
                        return (
                            <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-primary hover:underline"
                            >
                                {name}
                                <ExternalLink className="h-3 w-3 shrink-0" />
                            </a>
                        );
                    }
                    return <span className="text-muted-foreground">—</span>;
                },
            },
            {
                id: 'type',
                label: 'Type',
                key: 'type',
                sortable: true,
                width: '100px',
                render: value => (
                    <Badge variant="outline" className="text-xs">
                        {value}
                    </Badge>
                ),
            },
            {
                id: 'title',
                label: 'Title',
                key: 'title',
                sortable: true,
                width: '180px',
                render: value => (
                    <span className="truncate block max-w-[180px]" title={value || ''}>
                        {value || 'Untitled'}
                    </span>
                ),
            },
            {
                id: 'content',
                label: 'Content',
                key: 'content',
                sortable: false,
                width: '240px',
                render: value => (
                    <span
                        className="line-clamp-2 text-muted-foreground text-sm"
                        title={value || ''}
                    >
                        {value || '—'}
                    </span>
                ),
            },
            {
                id: 'created_at',
                label: 'Created',
                key: 'created_at',
                sortable: true,
                width: '160px',
                render: value => (
                    <DateTimeFormatter
                        date={value}
                        format="datetime"
                        locale={tenantLocale}
                        className="text-sm"
                    />
                ),
            },
        ],
        [tenantLocale]
    );

    const handleBulkDeleteNotes = async () => {
        if (!id || selectedNotes.length === 0) return;

        const ok = await confirm({
            title: 'Delete notes?',
            description: `Are you sure you want to delete ${selectedNotes.length} notes? This action cannot be undone.`,
            confirmLabel: 'Delete',
            variant: 'destructive',
        });
        if (!ok) return;

        try {
            await bulkDeleteNotes.mutateAsync({ userId: id, noteIds: selectedNotes });
            enqueueSnackbar(`Successfully deleted ${selectedNotes.length} notes`, {
                variant: 'success',
            });
            setSelectedNotes([]);
        } catch (error: any) {
            const message = error.response?.data?.message || 'Failed to delete notes';
            enqueueSnackbar(message, { variant: 'error' });
        }
    };

    const handleDeleteAllNotes = async () => {
        if (!id) return;

        try {
            const result = await deleteAllNotes.mutateAsync({ userId: id, types: deleteAllTypes });
            if ((result.deleted_count ?? 0) === 0) {
                enqueueSnackbar('No matching items to delete', { variant: 'info' });
            } else {
                enqueueSnackbar(
                    `Successfully deleted ${result.deleted_count} item${result.deleted_count === 1 ? '' : 's'}`,
                    { variant: 'success' }
                );
            }
            setSelectedNotes([]);
            setDeleteAllDialogOpen(false);
        } catch (error: any) {
            const message = error.response?.data?.message || 'Failed to delete items';
            enqueueSnackbar(message, { variant: 'error' });
        }
    };

    // Handle password reset
    const handlePasswordReset = async () => {
        if (!user) return;

        if (passwordData.password.length < 8) {
            enqueueSnackbar('Password must be at least 8 characters long', { variant: 'error' });
            return;
        }

        try {
            await resetPassword.mutateAsync({
                id: user.id,
                password: passwordData.password,
            });

            enqueueSnackbar(`Password reset successfully for ${user.name}`, {
                variant: 'success',
            });

            setPasswordResetDialog(false);
            setPasswordData({ password: '' });
            setShowPassword(false);
        } catch (error: any) {
            const message = error.response?.data?.message || 'Failed to reset password';
            enqueueSnackbar(message, { variant: 'error' });
        }
    };

    const handleGeneratePassword = () => {
        const generated = generatePassword(16);
        setPasswordData({ password: generated });
    };

    if (userLoading || rolesLoading) {
        return (
            <PageContainer>
                <div className="flex items-center gap-4">
                    <Skeleton className="h-9 w-40 rounded-md" />
                    <div className="space-y-1">
                        <Skeleton className="h-8 w-40" />
                        <Skeleton className="h-4 w-64" />
                    </div>
                </div>
                <Card>
                    <CardHeader>
                        <Skeleton className="h-6 w-48" />
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {Array.from({ length: 10 }).map((_, i) => (
                                <div key={i} className="space-y-2">
                                    <Skeleton className="h-4 w-24" />
                                    <Skeleton className="h-9 w-full rounded-md" />
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </PageContainer>
        );
    }

    if (userError || !user) {
        return (
            <PageContainer>
                <PageHeader title="Edit Employee" backTo="/users" backLabel="Back to employees" />
                <Card>
                    <CardContent className="p-6">
                        <div className="text-center">
                            <p className="text-destructive">
                                Employee not found or you don't have permission to view it.
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <PageHeader
                title="Edit Employee"
                description="Update employee information and permissions"
                breadcrumbs={[{ label: 'Employees', to: '/users' }, { label: user.name }]}
                backTo="/users"
                backLabel="Back to employees"
            />

            {/* User Information Card */}
            <Card>
                <CardHeader>
                    <CardTitle>Employee Information</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Name */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-muted-foreground">
                                Name *
                            </label>
                            <InlineEdit
                                value={user.name}
                                onSave={value => handleFieldUpdate('name', value)}
                                permissionModule={ModulePermission.USERS}
                                className="text-lg font-semibold"
                                placeholder="Enter full name"
                            />
                        </div>

                        {/* Title */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-muted-foreground">
                                Job Title
                            </label>
                            <InlineEdit
                                value={user.title || ''}
                                onSave={value => handleFieldUpdate('title', value)}
                                permissionModule={ModulePermission.USERS}
                                placeholder="Enter job title"
                            />
                        </div>

                        {/* Email */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-muted-foreground">
                                Email *
                            </label>
                            <InlineEdit
                                value={user.email}
                                onSave={value => handleFieldUpdate('email', value)}
                                type="text"
                                permissionModule={ModulePermission.USERS}
                                placeholder="Enter email address"
                            />
                        </div>

                        {/* Role */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-muted-foreground">
                                Role *
                            </label>
                            {rolesLoading ? (
                                <div className="flex items-center gap-2">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    <span className="text-sm text-muted-foreground">
                                        Loading roles...
                                    </span>
                                </div>
                            ) : (
                                <InlineEdit
                                    value={user.role?.name || ''}
                                    onSave={value => {
                                        const selectedRole = roles?.find(
                                            role => role.name === value
                                        );
                                        handleFieldUpdate('role_id', selectedRole?.id || '');
                                    }}
                                    type="select"
                                    selectOptions={
                                        roles?.map(role => ({
                                            value: role.name,
                                            label: role.name,
                                        })) || []
                                    }
                                    permissionModule={ModulePermission.USERS}
                                    placeholder="Select role"
                                />
                            )}
                        </div>

                        {/* Status */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-muted-foreground">
                                Account Status
                            </label>
                            <div className="flex items-center justify-between p-3 bg-muted rounded-md">
                                <div className="flex items-center gap-3">
                                    <div className="flex items-center gap-2">
                                        <Switch
                                            checked={user.status === 'active'}
                                            onCheckedChange={checked => {
                                                const newStatus = checked ? 'active' : 'inactive';
                                                handleFieldUpdate('status', newStatus);
                                            }}
                                            disabled={updateUserField.isPending}
                                        />
                                        <span className="text-sm font-medium">
                                            {user.status === 'active' ? 'Active' : 'Blocked'}
                                        </span>
                                    </div>
                                    <Badge
                                        variant={
                                            user.status === 'active' ? 'default' : 'destructive'
                                        }
                                        className="text-xs"
                                    >
                                        {user.status === 'active' ? 'Can Login' : 'Blocked'}
                                    </Badge>
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    {user.status === 'active'
                                        ? 'User can access the system'
                                        : 'User cannot log in'}
                                </div>
                            </div>
                        </div>

                        {/* Phone */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-muted-foreground">
                                Phone Number
                            </label>
                            <InlineEdit
                                value={user.phone ?? ''}
                                onSave={value => handleFieldUpdate('phone', value || null)}
                                type="text"
                                permissionModule={ModulePermission.USERS}
                                placeholder="Enter phone number"
                            />
                        </div>

                        {/* WhatsApp Number */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-muted-foreground">
                                WhatsApp Number
                            </label>
                            <InlineEdit
                                value={user.whatsapp_number ?? ''}
                                onSave={value =>
                                    handleFieldUpdate('whatsapp_number', value || null)
                                }
                                type="text"
                                permissionModule={ModulePermission.USERS}
                                placeholder="Enter WhatsApp number"
                            />
                        </div>

                        {/* Telegram */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-muted-foreground">
                                Telegram
                            </label>
                            <InlineEdit
                                value={user.telegram ?? ''}
                                onSave={value => handleFieldUpdate('telegram', value || null)}
                                type="text"
                                permissionModule={ModulePermission.USERS}
                                placeholder="Enter Telegram handle or number"
                            />
                        </div>

                        {/* PBX Extension (for click-to-call when using Arestik PBX) */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-muted-foreground">
                                PBX Extension
                            </label>
                            <InlineEdit
                                value={user.extension ?? ''}
                                onSave={value => handleFieldUpdate('extension', value || null)}
                                type="text"
                                permissionModule={ModulePermission.USERS}
                                placeholder="e.g. 502 (for click-to-call with Arestik PBX)"
                            />
                        </div>

                        {/* Supervisor */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-muted-foreground">
                                Supervisor
                            </label>
                            <InlineEdit
                                value={user.supervisor?.name || ''}
                                onSave={value => {
                                    if (!value) {
                                        handleFieldUpdate('supervisor_id', null);
                                    } else {
                                        const selectedUser = users.find(u => u.name === value);
                                        handleFieldUpdate(
                                            'supervisor_id',
                                            selectedUser?.id || null
                                        );
                                    }
                                }}
                                type="select"
                                selectOptions={[
                                    ...users
                                        .filter(u => u.id !== user.id) // Prevent self-assignment
                                        .map(u => ({
                                            value: u.name,
                                            label: `${u.name}${u.title ? ` (${u.title})` : ''}`,
                                        })),
                                ]}
                                permissionModule={ModulePermission.USERS}
                                placeholder="Select supervisor"
                            />
                        </div>

                        {/* Created At */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-muted-foreground">
                                Created At
                            </label>
                            <div className="p-3 bg-muted rounded-md">
                                <DateTimeFormatter date={user.created_at} className="text-sm" />
                            </div>
                        </div>

                        {/* Last Updated */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-muted-foreground">
                                Last Updated
                            </label>
                            <div className="p-3 bg-muted rounded-md">
                                <DateTimeFormatter date={user.updated_at} className="text-sm" />
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Password Reset Card */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Lock className="h-5 w-5" />
                        Password Management
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-full bg-info/10">
                                    <Lock className="h-5 w-5 text-info" />
                                </div>
                                <div>
                                    <h4 className="font-medium">Reset Password</h4>
                                    <p className="text-sm text-muted-foreground">
                                        Set a new password for {user.name}
                                    </p>
                                </div>
                            </div>
                            <Button
                                variant="outline"
                                onClick={() => setPasswordResetDialog(true)}
                                className="gap-2"
                            >
                                <Lock className="h-4 w-4" />
                                Reset Password
                            </Button>
                        </div>

                        {user.status !== 'active' && (
                            <div className="flex items-center gap-3 p-4 bg-warning/10 border border-warning/20 rounded-lg">
                                <AlertTriangle className="h-5 w-5 text-warning" />
                                <div className="text-sm text-warning">
                                    <strong>Note:</strong> Blocked employees cannot log in to the
                                    system. Use the toggle above to restore access.
                                </div>
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Notes Management Card */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        Notes Management
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-full bg-warning/10">
                                    <FileText className="h-5 w-5 text-warning" />
                                </div>
                                <div>
                                    <h4 className="font-medium">Manage Employee Notes</h4>
                                    <p className="text-sm text-muted-foreground">
                                        View and bulk delete notes created by {user.name}
                                    </p>
                                </div>
                            </div>
                            <Button
                                variant="outline"
                                onClick={() => setShowNotesManagement(!showNotesManagement)}
                                className="gap-2"
                            >
                                <FileText className="h-4 w-4" />
                                {showNotesManagement ? 'Hide Notes' : 'Show Notes'}
                            </Button>
                        </div>

                        {showNotesManagement && (
                            <div className="space-y-4">
                                <div className="flex flex-wrap items-center gap-3">
                                    <Input
                                        placeholder="Search notes..."
                                        value={notesSearch}
                                        onChange={e => {
                                            setNotesSearch(e.target.value);
                                            setNotesPage(1);
                                        }}
                                        className="max-w-xs h-9"
                                    />
                                    <Select
                                        value={notesTypeFilter}
                                        onValueChange={value => {
                                            setNotesTypeFilter(value);
                                            setNotesPage(1);
                                        }}
                                    >
                                        <SelectTrigger className="w-[140px] h-9">
                                            <SelectValue placeholder="Type" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">All types</SelectItem>
                                            <SelectItem value="note">Note</SelectItem>
                                            <SelectItem value="task">Task</SelectItem>
                                            <SelectItem value="activity">Activity</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    {canDeleteNotes &&
                                        (notesTypeFilter === 'all' ||
                                            (userNotes?.meta?.total ?? 0) > 0) && (
                                            <Button
                                                variant="destructive"
                                                size="sm"
                                                onClick={() => setDeleteAllDialogOpen(true)}
                                                disabled={deleteAllNotes.isPending}
                                                className="gap-2"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                                {deleteAllLabel}
                                            </Button>
                                        )}
                                    {selectedNotes.length > 0 && (
                                        <Button
                                            variant="destructive"
                                            size="sm"
                                            onClick={handleBulkDeleteNotes}
                                            disabled={bulkDeleteNotes.isPending}
                                            className="gap-2"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                            Delete Selected ({selectedNotes.length})
                                        </Button>
                                    )}
                                </div>

                                <DataTable
                                    data={userNotes?.data ?? []}
                                    columns={notesColumns}
                                    isLoading={notesLoading}
                                    sortConfig={notesSort}
                                    onSort={key => {
                                        setNotesSort(prev => ({
                                            key: key as keyof UserNote,
                                            direction:
                                                prev.key === key && prev.direction === 'desc'
                                                    ? 'asc'
                                                    : 'desc',
                                        }));
                                        setNotesPage(1);
                                    }}
                                    filters={{}}
                                    onFilterChange={() => {}}
                                    selectedItems={selectedNotes}
                                    onSelectionChange={setSelectedNotes}
                                    getItemId={note => note.id}
                                    emptyMessage="No notes found for this employee"
                                    loadingMessage="Loading notes..."
                                    showSelection={true}
                                    showActions={false}
                                    showAdvancedFilters={false}
                                    showColumnSettings={false}
                                />

                                {userNotes?.meta &&
                                    (userNotes.meta.last_page > 1 || notesPageSize !== 25) && (
                                        <Card>
                                            <CardContent className="p-4">
                                                <div className="flex items-center justify-between flex-wrap gap-4">
                                                    <div className="text-sm text-muted-foreground">
                                                        Showing{' '}
                                                        {(userNotes.meta.current_page - 1) *
                                                            userNotes.meta.per_page +
                                                            1}{' '}
                                                        to{' '}
                                                        {Math.min(
                                                            userNotes.meta.current_page *
                                                                userNotes.meta.per_page,
                                                            userNotes.meta.total
                                                        )}{' '}
                                                        of {userNotes.meta.total} notes
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-sm text-muted-foreground">
                                                            Show:
                                                        </span>
                                                        <Select
                                                            value={notesPageSize.toString()}
                                                            onValueChange={v => {
                                                                setNotesPageSize(parseInt(v, 10));
                                                                setNotesPage(1);
                                                            }}
                                                        >
                                                            <SelectTrigger className="w-20 h-9">
                                                                <SelectValue />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                <SelectItem value="10">
                                                                    10
                                                                </SelectItem>
                                                                <SelectItem value="25">
                                                                    25
                                                                </SelectItem>
                                                                <SelectItem value="50">
                                                                    50
                                                                </SelectItem>
                                                                <SelectItem value="100">
                                                                    100
                                                                </SelectItem>
                                                            </SelectContent>
                                                        </Select>
                                                        <div className="flex gap-1">
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() =>
                                                                    setNotesPage(p =>
                                                                        Math.max(1, p - 1)
                                                                    )
                                                                }
                                                                disabled={
                                                                    userNotes.meta.current_page <= 1
                                                                }
                                                            >
                                                                Previous
                                                            </Button>
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() =>
                                                                    setNotesPage(p =>
                                                                        Math.min(
                                                                            userNotes.meta
                                                                                .last_page,
                                                                            p + 1
                                                                        )
                                                                    )
                                                                }
                                                                disabled={
                                                                    userNotes.meta.current_page >=
                                                                    userNotes.meta.last_page
                                                                }
                                                            >
                                                                Next
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    )}
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Password Reset Dialog */}
            <Dialog open={passwordResetDialog} onOpenChange={setPasswordResetDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Reset Password for {user?.name}</DialogTitle>
                        <DialogDescription>
                            Enter a new password for this employee. The password must be at least 8
                            characters long.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label
                                    htmlFor="password"
                                    className="text-sm font-medium text-muted-foreground"
                                >
                                    New Password
                                </label>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleGeneratePassword}
                                    className="h-8 text-xs"
                                >
                                    <RefreshCw className="h-3 w-3" />
                                    Generate
                                </Button>
                            </div>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={passwordData.password}
                                    onChange={e =>
                                        setPasswordData(prev => ({
                                            ...prev,
                                            password: e.target.value,
                                        }))
                                    }
                                    placeholder="Enter new password"
                                    minLength={8}
                                    className={`pr-10 ${
                                        passwordData.password.length > 0 &&
                                        passwordData.password.length < 8
                                            ? 'border-destructive'
                                            : ''
                                    }`}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                >
                                    {showPassword ? (
                                        <EyeOff className="h-4 w-4" />
                                    ) : (
                                        <Eye className="h-4 w-4" />
                                    )}
                                </button>
                            </div>
                            {passwordData.password.length > 0 && (
                                <div className="mt-1">
                                    <div className="flex items-center gap-2">
                                        <div
                                            className={`h-2 w-16 rounded-full ${
                                                passwordData.password.length >= 8
                                                    ? 'bg-success'
                                                    : 'bg-muted'
                                            }`}
                                        />
                                        <span
                                            className={`text-xs ${
                                                passwordData.password.length >= 8
                                                    ? 'text-success'
                                                    : 'text-muted-foreground'
                                            }`}
                                        >
                                            {passwordData.password.length >= 8 ? 'Strong' : 'Weak'}
                                        </span>
                                    </div>
                                    <p
                                        className={`text-xs mt-1 ${
                                            passwordData.password.length >= 8
                                                ? 'text-success'
                                                : 'text-destructive'
                                        }`}
                                    >
                                        {passwordData.password.length >= 8
                                            ? 'Password meets minimum requirements'
                                            : 'Password must be at least 8 characters long'}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setPasswordResetDialog(false);
                                setPasswordData({ password: '' });
                                setShowPassword(false);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handlePasswordReset}
                            disabled={resetPassword.isPending || passwordData.password.length < 8}
                        >
                            {resetPassword.isPending ? 'Resetting...' : 'Reset Password'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={deleteAllDialogOpen} onOpenChange={setDeleteAllDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{deleteAllLabel}?</AlertDialogTitle>
                        <AlertDialogDescription>{deleteAllDescription}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleteAllNotes.isPending}>
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteAllNotes}
                            disabled={deleteAllNotes.isPending}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {deleteAllNotes.isPending ? 'Deleting...' : deleteAllLabel}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </PageContainer>
    );
};

export default UserEditPage;
